import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";

/**
 * DataTable
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.9 + §3.2
 *
 * Extended over the legacy two-column shape with backward-compatible adds:
 *
 *   - `tableKey`         — when set, column visibility persists to
 *                          `localStorage[datatable:<tableKey>:visibility]`.
 *   - `hideable`         — per-column flag; the toolbar's Columns menu only
 *                          shows columns flagged `hideable: true`.
 *   - `defaultHidden`    — start hidden until user toggles via the menu.
 *   - `mobileCard`       — optional render function for screens narrower than
 *                          `md`. Replaces the full row with a card surface.
 *   - `expandedRow`      — optional render function to show an expanded
 *                          drawer below the row when clicked. Mutually
 *                          exclusive with `onRowClick`.
 *   - `emptyIcon`, `emptyMessage` — the empty state already supported; both
 *                          still work.
 *
 * Existing callers (14 pages today) only use `{ data, columns, isLoading,
 * onRowClick, emptyMessage, pageSize, totalCount, currentPage, onPageChange,
 * onPageSizeChange }` — all kept verbatim.
 *
 * Density per spec: `<TableHead>` h-9, `<TableCell>` py-2 px-3. No zebra
 * striping. No sticky header at this level — the page chrome handles that.
 */

export interface DataTableColumn<T> {
  key: string;
  header: string;
  sortable?: boolean;
  /** Eligible for the toolbar's Columns visibility menu. */
  hideable?: boolean;
  /** Off by default until user opts in. */
  defaultHidden?: boolean;
  className?: string;
  cell?: (row: T) => React.ReactNode;
}

interface DataTableProps<T extends { id: string }> {
  data: T[];
  columns: DataTableColumn<T>[];
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;

  /** Pagination (server-side). */
  pageSize?: number;
  totalCount?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;

  /** Stable id for column-visibility persistence. */
  tableKey?: string;
  /**
   * Read column visibility from outside (e.g. `HeroToolbar`'s Columns menu).
   * When omitted, `DataTable` reads from `localStorage` keyed by `tableKey`.
   */
  columnVisibility?: Record<string, boolean>;
  /** Called when the DataTable mutates visibility internally. */
  onColumnVisibilityChange?: (next: Record<string, boolean>) => void;

  /** Mobile-only render: replaces the row with a card on `< md`. */
  mobileCard?: (row: T) => React.ReactNode;

  /** Inline expanded panel below the clicked row. */
  expandedRow?: (row: T) => React.ReactNode;

  /** External sort control (otherwise managed internally for backwards compat). */
  sortKey?: string | null;
  sortDirection?: "asc" | "desc";
  onSort?: (key: string, direction: "asc" | "desc") => void;

  /** Stable test id appended to the wrapper. */
  "data-testid"?: string;
}

export function DataTable<T extends { id: string }>({
  data,
  columns,
  isLoading = false,
  onRowClick,
  emptyMessage = "No data found",
  emptyIcon,
  pageSize = 25,
  totalCount,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  tableKey,
  columnVisibility: externalVisibility,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onColumnVisibilityChange: _onColumnVisibilityChange,
  mobileCard,
  expandedRow,
  sortKey: externalSortKey,
  sortDirection: externalSortDirection,
  onSort,
  "data-testid": dataTestId,
}: DataTableProps<T>) {
  // ─── Sort (internal fallback when no external control) ──────────────────
  const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] =
    useState<"asc" | "desc">("asc");
  const sortKey =
    externalSortKey !== undefined ? externalSortKey : internalSortKey;
  const sortDirection = externalSortDirection ?? internalSortDirection;

  const handleSort = (key: string) => {
    const nextDirection =
      sortKey === key && sortDirection === "asc" ? "desc" : "asc";
    if (onSort) {
      onSort(key, nextDirection);
    } else {
      setInternalSortKey(key);
      setInternalSortDirection(nextDirection);
    }
  };

  // ─── Column visibility ──────────────────────────────────────────────────
  const [internalVisibility, setInternalVisibility] = useState<
    Record<string, boolean>
  >(() => {
    // Hydrate from localStorage on first mount when tableKey is set
    const seed: Record<string, boolean> = {};
    for (const col of columns) {
      if (col.defaultHidden) seed[col.key] = false;
    }
    if (!tableKey || typeof window === "undefined") return seed;
    try {
      const raw = window.localStorage.getItem(
        `datatable:${tableKey}:visibility`
      );
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return { ...seed, ...parsed };
        }
      }
    } catch {
      // ignore parse / storage errors
    }
    return seed;
  });

  // Keep internal state in sync with external `columnVisibility` prop.
  useEffect(() => {
    if (externalVisibility) setInternalVisibility(externalVisibility);
  }, [externalVisibility]);

  const visibility = externalVisibility ?? internalVisibility;

  const visibleColumns = useMemo(
    () =>
      columns.filter((col) => {
        const explicit = visibility[col.key];
        if (explicit === undefined) {
          return !col.defaultHidden;
        }
        return explicit !== false;
      }),
    [columns, visibility]
  );

  // ─── Expanded rows ──────────────────────────────────────────────────────
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 1;

  // ─── Loading ────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-4" data-testid={dataTestId}>
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={col.className}>
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full max-w-[150px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      {/* Desktop / tablet table */}
      <div
        className={cn(
          "rounded-md border bg-card",
          mobileCard ? "hidden md:block" : ""
        )}
      >
        <Table>
          <TableHeader>
            <TableRow>
              {expandedRow ? <TableHead className="w-8 p-0" /> : null}
              {visibleColumns.map((col) => (
                <TableHead key={col.key} className={col.className}>
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className={cn(
                        "inline-flex items-center gap-1 -ml-2 h-8 px-2 rounded-sm",
                        "hover-elevate active-elevate-2",
                        "text-left font-medium"
                      )}
                      data-testid={`button-sort-${col.key}`}
                    >
                      {col.header}
                      {sortKey === col.key ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3.5 w-3.5" />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    visibleColumns.length + (expandedRow ? 1 : 0)
                  }
                  className="h-24 text-center"
                >
                  <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-8">
                    {emptyIcon}
                    <p className="text-sm">{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const isExpanded = expandedIds.has(row.id);
                return (
                  <RowAndExpansion
                    key={row.id}
                    row={row}
                    visibleColumns={visibleColumns}
                    isExpanded={isExpanded}
                    expandedRow={expandedRow}
                    onRowClick={onRowClick}
                    onToggleExpanded={toggleExpanded}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile card fallback */}
      {mobileCard ? (
        <div className="md:hidden space-y-2">
          {data.length === 0 ? (
            <div className="rounded-md border bg-card p-6 text-center">
              <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                {emptyIcon}
                <p className="text-sm">{emptyMessage}</p>
              </div>
            </div>
          ) : (
            data.map((row) => (
              <div
                key={row.id}
                onClick={() => onRowClick?.(row)}
                className={cn(
                  "rounded-md border bg-card p-3",
                  onRowClick && "cursor-pointer hover-elevate active-elevate-2"
                )}
                data-testid={`mobile-card-${row.id}`}
              >
                {mobileCard(row)}
              </div>
            ))
          )}
        </div>
      ) : null}

      {/* Pagination */}
      {totalCount && totalCount > 0 ? (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
            results
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                Rows per page:
              </span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => onPageSizeChange?.(Number(value))}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange?.(1)}
                disabled={currentPage === 1}
                aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange?.(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1 px-2">
                <span className="text-xs">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange?.(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange?.(totalPages)}
                disabled={currentPage === totalPages}
                aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Inner row + optional expansion drawer. Pulled out so we don't read
 * `expandedIds` from inside a `data.map` closure (cleaner profiling, and
 * fewer re-renders if React adds row-level memoization later).
 */
function RowAndExpansion<T extends { id: string }>({
  row,
  visibleColumns,
  isExpanded,
  expandedRow,
  onRowClick,
  onToggleExpanded,
}: {
  row: T;
  visibleColumns: DataTableColumn<T>[];
  isExpanded: boolean;
  expandedRow?: (row: T) => React.ReactNode;
  onRowClick?: (row: T) => void;
  onToggleExpanded: (id: string) => void;
}) {
  const colSpan = visibleColumns.length + (expandedRow ? 1 : 0);
  return (
    <>
      <TableRow
        onClick={() => {
          if (expandedRow) {
            onToggleExpanded(row.id);
          } else {
            onRowClick?.(row);
          }
        }}
        className={cn(
          (onRowClick || expandedRow) && "cursor-pointer",
          isExpanded && "bg-muted/30"
        )}
        data-testid={`table-row-${row.id}`}
      >
        {expandedRow ? (
          <TableCell className="w-8 p-0 text-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpanded(row.id);
              }}
              className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover-elevate active-elevate-2"
              aria-label={isExpanded ? "Collapse row" : "Expand row"}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </TableCell>
        ) : null}
        {visibleColumns.map((col) => (
          <TableCell key={col.key} className={col.className}>
            {col.cell
              ? col.cell(row)
              : ((row as unknown as Record<string, unknown>)[col.key] as React.ReactNode)}
          </TableCell>
        ))}
      </TableRow>
      {expandedRow && isExpanded ? (
        <TableRow className="bg-muted/30 border-t-0">
          <TableCell colSpan={colSpan} className="px-4 py-3">
            {expandedRow(row)}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
